/**
 * ADR-0216 §3.4 — Atlas Reports list at /app/workspace/atlas-reports.
 *
 * Read-only: an Atlas Report is authored through the door with AI support (any
 * MCP client, not one in particular), so there is no create button; the empty
 * state says so. Names, never ids.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState } from "../components/States.js";

export type AtlasReportListItem = {
  report_id: string;
  name: string;
  slug: string;
  status: "active" | "archived";
  operation: string | null;
  active_version_no: number | null;
  draft_version_no: number | null;
  last_run: { status: string; started_at: string | null } | null;
  subscription_count: number;
  /** Amendment 8: the owners' names, in the order they were added. */
  owners: string[];
  retention_days: number;
  updated_at: string | null;
};

export function runStatusPill(status: string | null | undefined): JSX.Element {
  if (!status) return <span className="muted">—</span>;
  if (status === "succeeded") return <span className="pill pillOk">succeeded</span>;
  if (status === "failed") return <span className="pill pillBad">failed</span>;
  if (status === "running" || status === "queued") return <span className="pill">{status}</span>;
  return <span className="pill">{status}</span>;
}

export function TenantAtlasReports() {
  const [items, setItems] = useState<AtlasReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ reports: AtlasReportListItem[] }>("/api/tenant/atlas-reports");
      setItems(data.reports);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageLayout title="Atlas Reports">
      <div className="card adminList">
        {error ? <ErrorState detail={error} /> : null}
        {!error && loading ? <LoadingState /> : null}
        {!error && !loading && items.length === 0 ? (
          <div className="adminListEmpty">Atlas Reports are built with AI support. No reports yet.</div>
        ) : null}
        {!error && !loading && items.length > 0 ? (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Operation</th>
                  <th>Active version</th>
                  <th>Last run</th>
                  <th>Subscriptions</th>
                  <th>Owners</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.report_id}>
                    <td>
                      <Link className="adminListName" to={`/app/workspace/atlas-reports/${encodeURIComponent(r.report_id)}`}>
                        {r.name}
                      </Link>
                    </td>
                    <td>{r.operation ?? <span className="muted">—</span>}</td>
                    <td>{r.active_version_no !== null ? `v${r.active_version_no}` : <span className="muted">—</span>}</td>
                    <td>{runStatusPill(r.last_run?.status ?? null)}</td>
                    <td>{r.subscription_count}</td>
                    <td>{r.owners.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}
